import React from 'react';

interface KpiCardProps {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string;
  up?: boolean | null; // null = neutral (no arrow / muted color)
  compareLabel?: string;
  featured?: boolean;
  live?: boolean;
  spark?: number[];
  /** Small muted sub-line under the value — use to clarify scope/provenance of the metric. */
  hint?: string;
  style?: React.CSSProperties;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  unit,
  delta,
  up,
  compareLabel,
  featured,
  live,
  spark,
  hint,
  style,
}) => {
  const cardBase: React.CSSProperties = {
    background: featured
      ? 'var(--cg-gradient-hero)'
      : 'var(--bg-surface-1)',
    border: featured ? 'none' : '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    padding: '18px 20px',
    boxShadow: featured
      ? 'var(--shadow-featured)'
      : 'var(--shadow-xs)',
    color: featured ? '#fff' : 'var(--fg-1)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    position: 'relative',
    overflow: 'hidden',
    ...style,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: featured ? 'rgba(255,255,255,0.78)' : 'var(--fg-4)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };

  const valueStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: 30,
    fontWeight: 700,
    color: featured ? '#fff' : 'var(--fg-1)',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.022em',
    marginTop: 6,
    lineHeight: 1.05,
  };

  const deltaColor = featured
    ? '#fff'
    : up === null || up === undefined
      ? 'var(--fg-4)'
      : up
        ? 'var(--success)'
        : 'var(--danger)';

  return (
    <div style={cardBase}>
      <div style={labelStyle}>
        {live && (
          <span
            style={{
              display: 'inline-block',
              width: 6, height: 6, borderRadius: '50%',
              background: featured ? '#F2A93B' : 'var(--success)',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
              animation: 'cgPulseDot 1.6s ease-in-out infinite',
            }}
          />
        )}
        {label}
      </div>
      <div style={valueStyle}>
        <span>{value}</span>
        {unit && <span style={{ fontSize: 18, marginLeft: 2, opacity: 0.78 }}>{unit}</span>}
      </div>
      {delta && (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: deltaColor, marginTop: 2 }}>
          {up === true ? '▲ ' : up === false ? '▼ ' : ''}{delta}
          {compareLabel && (
            <span style={{
              fontWeight: 500,
              marginLeft: 8,
              color: featured ? 'rgba(255,255,255,0.7)' : 'var(--fg-4)',
            }}>
              {compareLabel}
            </span>
          )}
        </div>
      )}
      {spark && spark.length > 0 && (
        <Sparkline data={spark} featured={!!featured} />
      )}
      {hint && (
        <div style={{
          fontSize: 10.5,
          fontWeight: 500,
          lineHeight: 1.3,
          marginTop: 6,
          color: featured ? 'rgba(255,255,255,0.72)' : 'var(--fg-4)',
        }}>
          {hint}
        </div>
      )}
    </div>
  );
};

const Sparkline: React.FC<{ data: number[]; featured: boolean }> = ({ data, featured }) => {
  const w = 200, h = 36, pad = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const points = data
    .map((v, i) => {
      const x = pad + (i / (data.length - 1)) * (w - 2 * pad);
      const y = h - pad - ((v - min) / range) * (h - 2 * pad);
      return `${x},${y}`;
    })
    .join(' ');
  const stroke = featured ? '#F2A93B' : 'var(--accent)';
  const fill = featured ? 'rgba(242,169,59,0.22)' : 'rgba(14,31,58,0.10)';
  const areaPoints = `${pad},${h - pad} ${points} ${w - pad},${h - pad}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 36, marginTop: 8, display: 'block' }}>
      <polygon points={areaPoints} fill={fill} />
      <polyline fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" points={points} />
    </svg>
  );
};

export default KpiCard;
